// MorrowOS entitlements — the client's source of truth for "what may this
// subject do?", fetched from the backend-authoritative engine at
//   GET /api/morrow/billing/entitlements
//
// This retires the old Clerk `has({plan})` guess (see morrowPlan.ts). The server
// resolves plan + overrides + grants + live usage; the client only reads it and
// asks `can(cap)` / `limit(cap)` / `remaining(cap)`. Nothing here hardcodes a
// plan — capability keys are the contract (mirrors apps/server billing catalog).
//
// A single module-level store backs every consumer (useEntitlements /
// useMorrowPlan) so one fetch serves the app; `refreshEntitlements()` re-pulls
// after a checkout/portal round-trip flips the subscription.
import { useSyncExternalStore } from "react";

import { apiUrl } from "./apiBase";
import { getClerkToken, whenClerkReady, getOwnerId } from "./clerkTokenBridge";

export type EntitlementCapabilityType = "boolean" | "quantity" | "usage" | "rate" | "storage";

/** One capability's effective allowance + current-period usage + catalog metadata. */
export interface EntitlementCapability {
  readonly capability: string;
  /** Set for boolean caps (on/off); null for numeric caps. */
  readonly bool: boolean | null;
  /** Numeric allowance (usage/quantity/rate/storage); null = unlimited. */
  readonly limit: number | null;
  readonly used: number;
  /** max(0, limit - used); null when unlimited. */
  readonly remaining: number | null;
  /** Whether the plan actually grants this — disambiguates unlimited vs not-included. */
  readonly granted: boolean;
  /** Catalog metadata (server-provided, so the UI never hardcodes labels). */
  readonly name: string;
  readonly type: EntitlementCapabilityType;
  readonly unit: string | null;
  readonly category: string | null;
  readonly description: string | null;
  /** Announced but not built/enforced yet — render "Coming soon". */
  readonly comingSoon: boolean;
  /** False when `used` isn't a real reading (nothing records it yet) — don't show it. */
  readonly metered: boolean;
  /** The plan's own allowance before extra credit (same unit as `limit`). */
  readonly planLimit: number | null;
  /** Extra allowance from grants/top-ups on top of the plan. */
  readonly extra: number;
}

const CAP_TYPES: readonly EntitlementCapabilityType[] = [
  "boolean",
  "quantity",
  "usage",
  "rate",
  "storage",
];

export interface EntitlementsViewer {
  readonly isOrg: boolean;
  readonly isAdmin: boolean;
  readonly usageVisible: boolean;
}

/**
 * An in-progress trial. Present only while `status = 'trialing'` on the server.
 * `cardless` = the no-credit-card trial (started via startTrial()); false = a
 * Stripe checkout trial that already has a card and will auto-bill at period end.
 */
export interface TrialState {
  readonly active: true;
  readonly endsAt: string;
  readonly daysLeft: number;
  readonly cardless: boolean;
}

export interface Entitlements {
  readonly subjectId: string;
  readonly audience: "individual" | "business";
  readonly tier: string;
  readonly plan: { readonly slug: string; readonly name: string; readonly priceCents: number };
  readonly trial: TrialState | null;
  readonly viewer: EntitlementsViewer;
  readonly capabilities: ReadonlyArray<EntitlementCapability>;
}

type StoreState = {
  readonly status: "idle" | "loading" | "loaded" | "error";
  readonly data: Entitlements | null;
};

let state: StoreState = { status: "idle", data: null };
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: StoreState): void {
  state = next;
  for (const listener of listeners) listener();
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function parseEntitlements(raw: unknown): Entitlements | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const planRec = (rec.plan && typeof rec.plan === "object" ? rec.plan : {}) as Record<
    string,
    unknown
  >;
  const capabilities = Array.isArray(rec.capabilities)
    ? rec.capabilities.map((entry) => {
        const c = (entry ?? {}) as Record<string, unknown>;
        const capKey = typeof c.capability === "string" ? c.capability : "";
        const type = CAP_TYPES.includes(c.type as EntitlementCapabilityType)
          ? (c.type as EntitlementCapabilityType)
          : "boolean";
        return {
          capability: capKey,
          bool: typeof c.bool === "boolean" ? c.bool : null,
          limit: typeof c.limit === "number" ? c.limit : null,
          used: num(c.used),
          remaining: typeof c.remaining === "number" ? c.remaining : null,
          granted: c.granted === true,
          name: typeof c.name === "string" && c.name.length > 0 ? c.name : capKey,
          type,
          unit: typeof c.unit === "string" ? c.unit : null,
          category: typeof c.category === "string" ? c.category : null,
          description: typeof c.description === "string" ? c.description : null,
          comingSoon: c.comingSoon === true,
          // Older servers don't send these: treat as metered with no extra credit.
          metered: c.metered !== false,
          planLimit:
            typeof c.planLimit === "number"
              ? c.planLimit
              : typeof c.limit === "number"
                ? c.limit
                : null,
          extra: num(c.extra),
        } satisfies EntitlementCapability;
      })
    : [];
  const viewerRec = (rec.viewer && typeof rec.viewer === "object" ? rec.viewer : {}) as Record<
    string,
    unknown
  >;
  const trialRec =
    rec.trial && typeof rec.trial === "object" ? (rec.trial as Record<string, unknown>) : null;
  const trial: TrialState | null =
    trialRec && typeof trialRec.endsAt === "string"
      ? {
          active: true,
          endsAt: trialRec.endsAt,
          daysLeft: num(trialRec.daysLeft),
          cardless: trialRec.cardless === true,
        }
      : null;
  return {
    subjectId: typeof rec.subjectId === "string" ? rec.subjectId : "",
    audience: rec.audience === "business" ? "business" : "individual",
    tier: typeof rec.tier === "string" ? rec.tier : "free",
    plan: {
      slug: typeof planRec.slug === "string" ? planRec.slug : "",
      name: typeof planRec.name === "string" ? planRec.name : "Free",
      priceCents: num(planRec.priceCents),
    },
    trial,
    viewer: {
      isOrg: viewerRec.isOrg === true,
      isAdmin: viewerRec.isAdmin !== false,
      // Default true so individuals (no viewer block) always see their own usage.
      usageVisible: viewerRec.usageVisible !== false,
    },
    capabilities: capabilities.filter((c) => c.capability.length > 0),
  };
}

/** Fetch (or re-fetch) the effective entitlement set into the shared store. */
export function refreshEntitlements(): Promise<void> {
  if (inFlight) return inFlight;
  publish({ status: state.data ? "loaded" : "loading", data: state.data });
  inFlight = (async () => {
    try {
      // Wait for Clerk to finish loading so the auth layer attaches the session
      // token (which carries the active org). Without this the very first fetch
      // races ahead of the token and resolves as an individual — the company plan
      // then never appears because nothing re-fetches. Bounded so a missing/slow
      // Clerk never hangs the page.
      await whenClerkReady();
      // Attach the Clerk token EXPLICITLY. In single-origin dev (paired browser)
      // the global fetch-auth layer bails (not cross-origin, not desktop), so the
      // `x-clerk-token` — which carries the active org — never rides along and the
      // backend resolves us as individual. Sending it here guarantees the org
      // reaches the entitlements endpoint regardless of environment.
      const token = await getClerkToken().catch(() => null);
      const ownerId = getOwnerId();
      const headers: Record<string, string> = { accept: "application/json" };
      if (token) headers["x-clerk-token"] = token;
      if (ownerId) headers["x-morrow-owner-id"] = ownerId;
      const res = await fetch(apiUrl("/api/morrow/billing/entitlements"), { headers });
      if (!res.ok) {
        publish({ status: "error", data: state.data });
        return;
      }
      const parsed = parseEntitlements(await res.json());
      publish({ status: "loaded", data: parsed });
    } catch {
      publish({ status: "error", data: state.data });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Force a Stripe→DB reconcile for the caller (the webhook-independent self-heal),
 * then publish the fresh entitlement set the reconcile returns. Use on the
 * checkout-return trip so a just-bought plan appears even when the webhook is
 * missed, late, or mis-attributed. Falls back to a plain refresh if /sync fails.
 */
export function syncEntitlements(): Promise<void> {
  return (async () => {
    try {
      await whenClerkReady();
      const token = await getClerkToken().catch(() => null);
      const ownerId = getOwnerId();
      const headers: Record<string, string> = { accept: "application/json" };
      if (token) headers["x-clerk-token"] = token;
      if (ownerId) headers["x-morrow-owner-id"] = ownerId;
      const res = await fetch(apiUrl("/api/morrow/billing/sync"), { method: "POST", headers });
      if (!res.ok) {
        await refreshEntitlements();
        return;
      }
      publish({ status: "loaded", data: parseEntitlements(await res.json()) });
    } catch {
      await refreshEntitlements();
    }
  })();
}

/**
 * The outcome of a start-trial attempt. Deliberately three-way, NOT a boolean: a caller
 * that offers "pay instead" on failure must only do so when the server DEFINITIVELY said
 * the subject can't trial — never on a transient error, or it asks an eligible-but-unlucky
 * user for a card for the cardless trial they were entitled to (the original bug, re-created).
 *   - "trialing"   → the subject is now on the trial; UI unlocked in place.
 *   - "ineligible" → a 200 from the server where the subject is NOT trialing (idempotent:
 *                    they already spent the trial, or already have a plan). A definitive "no".
 *   - "error"      → non-OK response (5xx/401/409) or a network failure. NOT a verdict —
 *                    the caller should let the user retry, never route them into checkout.
 */
export type StartTrialResult = {
  readonly outcome: "trialing" | "ineligible" | "error";
  /** On "error", the server's human message (e.g. the org-context 409 text) for the UI to surface. */
  readonly message?: string;
};

/**
 * Start the 7-day no-credit-card trial for the current individual, then publish the
 * fresh (now-trialing) entitlement set the route returns — so the UI unlocks with no
 * extra round-trip. Idempotent server-side: calling it when a plan/trial is already
 * active just returns the current summary. Falls back to a plain refresh on any error so
 * the store never goes stale. See `StartTrialResult` for why the three outcomes differ.
 */
export async function startTrial(planSlug?: string): Promise<StartTrialResult> {
  try {
    await whenClerkReady();
    const token = await getClerkToken().catch(() => null);
    const ownerId = getOwnerId();
    const headers: Record<string, string> = {
      accept: "application/json",
      "content-type": "application/json",
    };
    if (token) headers["x-clerk-token"] = token;
    if (ownerId) headers["x-morrow-owner-id"] = ownerId;
    // `planSlug` names WHICH individual plan to trial (cardless), so each plan card trials its
    // own real entitlements. Omitted (e.g. the onboarding reverse trial) → the default trial plan.
    const res = await fetch(apiUrl("/api/morrow/billing/start-trial"), {
      method: "POST",
      headers,
      body: JSON.stringify(planSlug ? { planSlug } : {}),
    });
    if (!res.ok) {
      // Transient / non-definitive (5xx, 401, 409 — e.g. an org account that can't self-grant a
      // personal trial). Keep the store fresh and hand the server's message back so the UI can
      // SHOW why instead of silently resetting. Never silently route into a card checkout.
      let message: string | undefined;
      try {
        const body = (await res.json()) as { error?: unknown };
        if (body && typeof body.error === "string") message = body.error;
      } catch {
        /* no JSON body */
      }
      await refreshEntitlements();
      return message !== undefined ? { outcome: "error", message } : { outcome: "error" };
    }
    const parsed = parseEntitlements(await res.json());
    publish({ status: "loaded", data: parsed });
    // A 200 is the server's definitive answer: trialing now, or (idempotent) not — in which
    // case the subject already spent their trial or already has a plan, i.e. ineligible.
    return { outcome: parsed?.trial?.active === true ? "trialing" : "ineligible" };
  } catch {
    await refreshEntitlements();
    return { outcome: "error", message: "Couldn't reach the server — please try again." };
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // First consumer triggers the initial load.
  if (state.status === "idle") void refreshEntitlements();
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = (): StoreState => state;

/** The MorrowOS entitlement API the UI gates on. */
export interface UseEntitlements {
  /** True once the first fetch has resolved (loaded or errored). */
  readonly isLoaded: boolean;
  readonly audience: "individual" | "business";
  readonly tier: string;
  readonly planName: string;
  /** Org/admin context for gating company-wide usage visibility. */
  readonly viewer: EntitlementsViewer;
  readonly planSlug: string;
  readonly planPriceCents: number;
  /** The running trial (card-less or Stripe), or null when not trialing. */
  readonly trial: TrialState | null;
  /** Boolean access check. Numeric caps are truthy when they have any allowance. */
  readonly can: (capability: string) => boolean;
  /** Numeric allowance for a capability; null = unlimited, 0 = no access. */
  readonly limit: (capability: string) => number | null;
  /** Live usage vs allowance for a metered capability. */
  readonly remaining: (capability: string) => {
    used: number;
    limit: number | null;
    remaining: number | null;
  };
  readonly capabilities: ReadonlyArray<EntitlementCapability>;
  readonly refresh: () => Promise<void>;
}

const findCap = (
  data: Entitlements | null,
  capability: string,
): EntitlementCapability | undefined => data?.capabilities.find((c) => c.capability === capability);

/**
 * Read the current subject's entitlements. Collapses to "free"/blocked while
 * loading or on error — the safe default (the server is the real authority).
 */
export function useEntitlements(): UseEntitlements {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const data = snapshot.data;

  return {
    isLoaded: snapshot.status === "loaded" || snapshot.status === "error",
    audience: data?.audience ?? "individual",
    tier: data?.tier ?? "free",
    planName: data?.plan.name ?? "Free",
    viewer: data?.viewer ?? { isOrg: false, isAdmin: true, usageVisible: true },
    planSlug: data?.plan.slug ?? "",
    planPriceCents: data?.plan.priceCents ?? 0,
    trial: data?.trial ?? null,
    can: (capability) => {
      const cap = findCap(data, capability);
      if (!cap) return false;
      if (cap.bool !== null) return cap.bool;
      return cap.limit === null || (cap.remaining ?? 0) > 0;
    },
    limit: (capability) => findCap(data, capability)?.limit ?? 0,
    remaining: (capability) => {
      const cap = findCap(data, capability);
      if (!cap) return { used: 0, limit: 0, remaining: 0 };
      return { used: cap.used, limit: cap.limit, remaining: cap.remaining };
    },
    capabilities: data?.capabilities ?? [],
    refresh: refreshEntitlements,
  };
}
